import { useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import {
    Badge,
    Body,
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldContent,
    FieldDescription,
    FieldLabel,
    Input,
    ListCard,
    ListCardRow,
    Spinner,
    Switch,
    Text,
} from "@athena/ui";
import { Check, Pencil, X } from "lucide-react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import {
    AutoJoinDomainImpactDocument,
    SetAutoJoinDomainsDocument,
    UpdateOrganizationDocument,
} from "../../graphql/organizations.generated";
import type { AutoJoinDomainImpactQuery, OrganizationByIdQuery } from "../../graphql/organizations.generated";

type Organization = NonNullable<OrganizationByIdQuery["organization"]>;
type Impact = AutoJoinDomainImpactQuery["autoJoinDomainImpact"][number];

/**
 * Auto-join domain management (ATL-169).
 *
 * The backend for this shipped complete — `setAutoJoinDomains`, and an `autoJoinDomainImpact` dry
 * run built specifically to feed a confirmation dialog. Neither had a caller: the admin app queried
 * `autoJoinEmailDomains` and rendered it nowhere, so the feature existed and no operator could
 * reach it. This is that missing surface.
 *
 * **Why the dialog is not optional here.** A mistyped domain enrolls every user holding it, and
 * unwinding means removing people from an org one at a time. `.claude/rules/ui.md` requires the
 * blast radius on screen *before* the action is accepted, with the count fetched rather than
 * guessed — which is what the dry run is for. The same reasoning is why the mutation is ADMIN/SYSTEM
 * rather than ORG_ADMIN: the dialog is the second line of defense, not the first.
 *
 * Conflicts are surfaced, not blocked. Two organizations claiming one domain is legitimate — a
 * university and its chapter both wanting the student — but it is usually a typo, so rival
 * claimants are named and the operator decides.
 */

/** Normalized the same way the backend stores it, so the diff check compares like with like. */
const normalize = (raw: string): string => raw.trim().toLowerCase().replace(/^@/, "");

/**
 * Rejects what is obviously not a domain before it reaches the dry run. Deliberately shallow — a
 * plausible-but-wrong domain is the dangerous case, and no regex catches that. The count in the
 * dialog is what does.
 */
const domainError = (value: string, existing: string[]): string | null => {
    if (!value) return "Enter a domain.";
    if (value.includes("@")) return "Enter the domain only, without the @ or an address.";
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(value)) return `"${value}" is not a valid domain.`;
    if (existing.includes(value)) return `${value} is already on the list.`;
    return null;
};

export function AutoJoinDomains({ org }: { org: Organization }) {
    const [
        editing,
        setEditing,
    ] = useState<boolean>(false);
    const [
        domains,
        setDomains,
    ] = useState<string[]>(org.autoJoinEmailDomains ?? []);
    const [
        draft,
        setDraft,
    ] = useState<string>("");
    const [
        error,
        setError,
    ] = useState<string | null>(null);
    /** Non-null once the dry run is back, which is what opens the dialog. */
    const [
        impact,
        setImpact,
    ] = useState<Impact[] | null>(null);

    const saved = org.autoJoinEmailDomains ?? [];
    const dirty = domains.length !== saved.length || domains.some((d) => !saved.includes(d));

    const [
        fetchImpact,
        { loading: checking },
    ] = useLazyQuery(AutoJoinDomainImpactDocument, { fetchPolicy: "network-only" });
    const [
        setAutoJoinDomains,
        { loading: saving },
    ] = useMutation(SetAutoJoinDomainsDocument);
    const [updateOrganization] = useMutation(UpdateOrganizationDocument);

    const addDraft = () => {
        const value = normalize(draft);
        const problem = domainError(value, domains);
        if (problem) {
            setError(problem);
            return;
        }
        setDomains([
            ...domains,
            value,
        ]);
        setDraft("");
        setError(null);
    };

    /**
     * Only newly added domains go to the dry run. Re-costing a domain that is already saved would
     * report zero (its holders are members already) and pad the dialog with rows that cannot change.
     */
    const added = domains.filter((d) => !saved.includes(d));
    const removed = saved.filter((d) => !domains.includes(d));

    const review = async () => {
        if (added.length === 0) {
            // Nothing to cost — a removal-only edit takes no one out of the org, so there is no
            // blast radius to confirm. Removing a domain only stops future joins.
            await commit();
            return;
        }
        try {
            const { data } = await fetchImpact({ variables: { organizationId: org.id, domains: added } });
            setImpact(data?.autoJoinDomainImpact ?? []);
        } catch (e) {
            Sentry.captureException(e);
            toast.error("Couldn't check what these domains would do. Nothing was changed.");
        }
    };

    const commit = async () => {
        try {
            await setAutoJoinDomains({ variables: { organizationId: org.id, domains } });
            setImpact(null);
            setEditing(false);
            toast.success("Auto-join domains saved.");
        } catch (e) {
            Sentry.captureException(e);
            toast.error("Couldn't save the domains. Please try again.");
        }
    };

    const toggleEnabled = async (next: boolean) => {
        try {
            await updateOrganization({ variables: { id: org.id, input: { autoJoinEnabled: next } } });
        } catch (e) {
            Sentry.captureException(e);
            toast.error("Couldn't change auto-join. Please try again.");
        }
    };

    const totalJoining = (impact ?? []).reduce((sum, row) => sum + row.willJoinCount, 0);
    const conflicts = (impact ?? []).filter((row) => row.alreadyClaimedBy.length > 0);

    return (
        <ListCard
            title="Auto-join domains"
            description="Anyone signing in with an email on these domains joins this organization automatically."
            action={
                <div className="flex flex-row gap-2">
                    {editing ? (
                        <>
                            <Button
                                variant="outline"
                                title="Review and save"
                                aria-label="Review and save"
                                disabled={!dirty || checking || saving}
                                onClick={review}
                            >
                                {checking || saving ? (
                                    <Spinner className="size-4" />
                                ) : (
                                    <Check className="text-muted-foreground h-4 w-4" />
                                )}
                            </Button>
                            <Button
                                variant="outline"
                                title="Cancel"
                                aria-label="Cancel"
                                disabled={checking || saving}
                                onClick={() => {
                                    setDomains(saved);
                                    setDraft("");
                                    setError(null);
                                    setEditing(false);
                                }}
                            >
                                <X className="text-muted-foreground h-4 w-4" />
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="outline"
                            title="Edit auto-join domains"
                            aria-label="Edit auto-join domains"
                            onClick={() => setEditing(true)}
                        >
                            <Pencil className="text-muted-foreground h-4 w-4" />
                        </Button>
                    )}
                </div>
            }
        >
            <ListCardRow>
                <Field orientation="horizontal" className="max-w-sm">
                    <Switch
                        id="auto-join-enabled"
                        checked={org.autoJoinEnabled ?? false}
                        onCheckedChange={toggleEnabled}
                    />
                    <FieldContent>
                        <FieldLabel htmlFor="auto-join-enabled">Enable auto-join</FieldLabel>
                        <FieldDescription>
                            Off keeps the domains listed but joins nobody, so a contract can be paused without retyping
                            them.
                        </FieldDescription>
                    </FieldContent>
                </Field>
            </ListCardRow>

            <ListCardRow>
                <Field>
                    <FieldLabel>Domains</FieldLabel>
                    <FieldDescription className="text-text-body text-xs">
                        Subdomains are not implied — list alumni.university.edu separately.
                    </FieldDescription>

                    {domains.length === 0 ? (
                        <Text muted className="text-sm">
                            No domains. Members are added manually.
                        </Text>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {domains.map((domain) => (
                                <Badge key={domain} variant="secondary" className="gap-1.5">
                                    {domain}
                                    {editing && (
                                        <button
                                            type="button"
                                            aria-label={`Remove ${domain}`}
                                            className="hover:text-destructive"
                                            onClick={() => setDomains(domains.filter((d) => d !== domain))}
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    )}
                                </Badge>
                            ))}
                        </div>
                    )}

                    {editing && (
                        <div className="flex flex-wrap items-start gap-2 pt-2">
                            <Input
                                aria-label="Add a domain"
                                placeholder="university.edu"
                                className="max-w-xs"
                                value={draft}
                                onChange={(e) => {
                                    setDraft(e.target.value);
                                    setError(null);
                                }}
                                onKeyDown={(e) => {
                                    // Enter adds the domain rather than submitting the surrounding form,
                                    // which would save without the confirmation step.
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addDraft();
                                    }
                                }}
                            />
                            <Button type="button" variant="outline" size="sm" className="w-auto" onClick={addDraft}>
                                Add
                            </Button>
                        </div>
                    )}

                    <div aria-live="polite">
                        {error && (
                            <Text role="alert" className="text-destructive text-xs">
                                {error}
                            </Text>
                        )}
                    </div>
                </Field>
            </ListCardRow>

            {/* Opened by the dry run coming back, so the numbers below are fetched, never guessed. */}
            <Dialog open={impact !== null} onOpenChange={(open) => !open && setImpact(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {totalJoining === 1 ? "Add 1 person to " : `Add ${totalJoining} people to `}
                            {org.name}?
                        </DialogTitle>
                        <DialogDescription>
                            Existing members are unaffected, and nobody is removed. People who sign up later are joined
                            when they first log in.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        {(impact ?? []).map((row) => (
                            <div key={row.domain} className="space-y-1">
                                <Body>
                                    <span className="font-semibold">{row.domain}</span> — joins {row.willJoinCount}{" "}
                                    {row.willJoinCount === 1 ? "person who is" : "people who are"} not already a member
                                </Body>
                                {row.alreadyClaimedBy.length > 0 && (
                                    <Text className="text-destructive text-xs">
                                        Also claimed by {row.alreadyClaimedBy.map((c) => c.name).join(", ")}. A matching
                                        user joins every claimant — check this is not a typo.
                                    </Text>
                                )}
                            </div>
                        ))}
                        {removed.length > 0 && (
                            <Text muted className="text-xs">
                                Removing {removed.join(", ")}. Nobody loses their membership; the domain just stops
                                joining new people.
                            </Text>
                        )}
                        {/*
                         * Only a summary when there is something to summarize. With one domain on
                         * screen it restated the warning directly above it, in the same red, which
                         * reads as two problems rather than one.
                         */}
                        {conflicts.length > 0 && (impact?.length ?? 0) > 1 && (
                            <Text className="text-destructive text-xs font-semibold">
                                {conflicts.length === 1
                                    ? "1 of these domains is already claimed elsewhere."
                                    : `${conflicts.length} of these domains are already claimed elsewhere.`}
                            </Text>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" className="w-auto" disabled={saving} onClick={() => setImpact(null)}>
                            Cancel
                        </Button>
                        {/* Repeats the action rather than saying "OK", so the click is deliberate. */}
                        <Button className="w-auto" disabled={saving} onClick={commit}>
                            {saving ? "Adding…" : totalJoining === 1 ? "Add 1 person" : `Add ${totalJoining} people`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </ListCard>
    );
}
